import { CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Typography
} from "antd";
import { FormProps } from "antd/es/form/Form";
import { TNotification } from "api/services/models/notification.types";
import { getAppUsers, sendNotification } from "api/services/NotificationService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { ReactComponent as TickSquareLinear } from "Icons/vuesax/tick-square.svg";
import { useEffect, useState } from "react";

type TNewNotificationModalProps = {
  onClose: (notifications?: TNotification[]) => void;
};

const Header = ({ onSubmit, onClose }: { onSubmit: () => void; onClose: () => void }) => (
  <Flex gap="middle" align="start" justify="space-between">
    <Typography.Title level={4}>{"New Notificaiton"}</Typography.Title>
    <Space size="small">
      <Button
        type="primary"
        htmlType="submit"
        className="default-button primary-btn"
        onClick={onSubmit}>
        <TickSquareLinear className="default-icon" />
        <span>Send</span>
      </Button>
      <Button
        style={{ border: "none" }}
        icon={<CloseOutlined style={{ fontSize: ".75rem" }} />}
        onClick={e => onClose()}></Button>
    </Space>
  </Flex>
);

const NewNotification = ({ onClose }: TNewNotificationModalProps) => {
  const { accountId, webDUser } = useTDAuthentication();
  const [users, setUsers] = useState<string[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [form] = Form.useForm();

  useEffect(() => {
    if (webDUser?.isInternalUser() && accountId) {
      setLoading(true);
      getAppUsers(accountId)
        .then(setUsers)
        .catch(setError)
        .finally(() => {
          setLoading(false);
          form.setFieldValue("to", null);
        });
    }
  }, [accountId]);

  const onSave = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(setError);
  };

  const onFinish: FormProps<{ to: string; message: string }>["onFinish"] = values => {
    setLoading(true);
    setError(null);
    sendNotification({
      fromUserId: webDUser?.user.userId!,
      toAccountId: accountId!,
      toUserId: values.to,
      messageText: values.message
    })
      .then(onClose)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      open={true}
      width={800}
      maskClosable={false}
      centered={true}
      closeIcon={null}
      footer={null}
      onCancel={() => onClose()}
      title={<Header onSubmit={onSave} onClose={onClose} />}>
      <Card
        style={{
          marginTop: "16px",
          padding: 0
        }}
        className="default-border"
        styles={{ body: { padding: "16px" } }}>
        {loading && (
          <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
            <Spin tip="Saving, please wait..." />
          </Flex>
        )}
        {error && <ErrorViewer error={error} />}
        <Typography.Text>{`From: ${webDUser?.user.userId}@${webDUser?.user.accountId}`}</Typography.Text>
        <Divider />
        <Form
          form={form}
          labelAlign="right"
          name="new_notification"
          onFinish={onFinish}
          layout="vertical"
          style={{ maxWidth: 800 }}
          validateTrigger={["onChange", "onBlur"]}>
          <Form.Item key={"to"} name={"to"} label={"To"} rules={[{ required: true }]}>
            <Select options={users?.map(u => ({ value: u, Label: u }))} />
          </Form.Item>

          <Form.Item
            key={"message"}
            name={"message"}
            label={"Message"}
            rules={[{ required: true, min: 3, max: 1024 }]}>
            <Input.TextArea showCount autoSize={{ minRows: 2, maxRows: 6 }} maxLength={1024} />
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
};

export default NewNotification;
